import React, { useState, useEffect, useCallback } from 'react';
import { FiMail, FiTrash2, FiCheck, FiPhone, FiUser, FiClock, FiInbox } from 'react-icons/fi';
import API from '../../api/axios';
import toast from 'react-hot-toast';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all'); // all | unread

  const fetchMessages = useCallback(() => {
    setLoading(true);
    const params = filter === 'unread' ? '?unread=true' : '';
    API.get(`/contact${params}`)
      .then(({ data }) => setMessages(data.messages))
      .catch(() => toast.error('Failed to load messages'))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const openMessage = async (msg) => {
    setSelected(msg);
    if (!msg.isRead) {
      try {
        await API.put(`/contact/${msg._id}`, { isRead: true });
        setMessages((prev) => prev.map((m) => m._id === msg._id ? { ...m, isRead: true } : m));
      } catch { /* silent */ }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await API.delete(`/contact/${id}`);
      toast.success('Message deleted');
      if (selected?._id === id) setSelected(null);
      fetchMessages();
    } catch {
      toast.error('Failed to delete message');
    }
  };

  const markUnread = async (id) => {
    try {
      await API.put(`/contact/${id}`, { isRead: false });
      setMessages((prev) => prev.map((m) => m._id === id ? { ...m, isRead: false } : m));
      if (selected?._id === id) setSelected((s) => ({ ...s, isRead: false }));
    } catch { /* silent */ }
  };

  const unreadCount = messages.filter((m) => !m.isRead).length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Messages</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {messages.length} message{messages.length !== 1 ? 's' : ''}
            {unreadCount > 0 && <span className="ml-1.5 px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs font-bold">{unreadCount} unread</span>}
          </p>
        </div>
        <div className="flex gap-2">
          {['all', 'unread'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors capitalize ${filter === f ? 'bg-primary-700 text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              {f === 'unread' ? `Unread (${unreadCount})` : 'All'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Message list */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="space-y-0 divide-y divide-gray-50">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="p-4 animate-pulse">
                  <div className="h-4 w-32 bg-gray-100 rounded mb-2" />
                  <div className="h-3 w-48 bg-gray-50 rounded" />
                </div>
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-16">
              <FiInbox size={36} className="mx-auto text-gray-200 mb-3" />
              <p className="text-gray-400 text-sm">No messages</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {messages.map((msg) => (
                <button
                  key={msg._id}
                  onClick={() => openMessage(msg)}
                  className={`w-full text-left px-4 py-4 hover:bg-gray-50 transition-colors ${selected?._id === msg._id ? 'bg-primary-50 border-l-2 border-primary-600' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {!msg.isRead && <span className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0 mt-1.5" />}
                      <div className="min-w-0">
                        <p className={`text-sm truncate ${!msg.isRead ? 'font-bold text-gray-800' : 'font-medium text-gray-700'}`}>
                          {msg.name}
                        </p>
                        {msg.subject && (
                          <p className="text-xs text-gray-500 truncate">{msg.subject}</p>
                        )}
                        <p className="text-xs text-gray-400 truncate mt-0.5">{msg.message}</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 flex-shrink-0">{new Date(msg.createdAt).toLocaleDateString()}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Message detail */}
        <div className="lg:col-span-3">
          {selected ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-start justify-between mb-5 gap-3">
                <div>
                  <h2 className="text-lg font-bold text-gray-800">{selected.subject || 'No Subject'}</h2>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                    <span className="flex items-center gap-1.5"><FiUser size={13} />{selected.name}</span>
                    {selected.email && <a href={`mailto:${selected.email}`} className="flex items-center gap-1.5 text-primary-600 hover:underline"><FiMail size={13} />{selected.email}</a>}
                    {selected.phone && <a href={`tel:${selected.phone}`} className="flex items-center gap-1.5 text-primary-600 hover:underline"><FiPhone size={13} />{selected.phone}</a>}
                    <span className="flex items-center gap-1.5"><FiClock size={13} />{new Date(selected.createdAt).toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {selected.isRead && (
                    <button
                      onClick={() => markUnread(selected._id)}
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded-xl transition-colors"
                      title="Mark as unread"
                    >
                      <FiMail size={16} />
                    </button>
                  )}
                  {selected.isRead && (
                    <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1.5 rounded-lg font-medium">
                      <FiCheck size={11} /> Read
                    </span>
                  )}
                  <button
                    onClick={() => handleDelete(selected._id)}
                    className="p-2 text-red-400 hover:bg-red-50 rounded-xl transition-colors"
                    title="Delete message"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-5 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {selected.message}
              </div>

              {/* Quick reply links */}
              <div className="mt-5 flex flex-wrap gap-3">
                {selected.email && (
                  <a
                    href={`mailto:${selected.email}?subject=Re: ${selected.subject || 'Your inquiry'}`}
                    className="flex items-center gap-2 bg-primary-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-800 transition-colors"
                  >
                    <FiMail size={14} /> Reply via Email
                  </a>
                )}
                {selected.phone && (
                  <a
                    href={`https://wa.me/${selected.phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 bg-green-500 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-green-600 transition-colors"
                  >
                    WhatsApp Reply
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex items-center justify-center py-24">
              <div className="text-center">
                <FiMail size={40} className="mx-auto text-gray-200 mb-3" />
                <p className="text-gray-400 text-sm">Select a message to view</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMessages;
